import React, { useEffect, useState } from "react";
import { Heart } from "lucide-react";
import api from "../lib/api";

/**
 * <RecentTributes /> — surfaces non-anonymous tribute donations as social proof.
 * Public, never shows dollar amounts or donor email — just "In honor of {name}"
 * style cards. Quietly renders nothing if no tributes have been made yet.
 */
const RecentTributes = () => {
    const [tributes, setTributes] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/tributes/public", { params: { limit: 6 } })
            .then((r) => setTributes(r.data || []))
            .catch(() => setTributes([]))
            .finally(() => setLoading(false));
    }, []);

    if (loading || tributes.length === 0) return null;

    return (
        <section className="bg-[#FDFBF7] py-20" data-testid="home-recent-tributes">
            <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="flex flex-col items-center text-center mb-10">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-[#F2D8D5] rounded-full mb-4">
                        <Heart className="w-3 h-3 text-[#E53935]" fill="#E53935" strokeWidth={0} />
                        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#E53935]">In honor &amp; in memory</span>
                    </div>
                    <h2 className="font-heading text-3xl sm:text-4xl text-[#1C252E] font-medium tracking-tight">
                        Recent tributes
                    </h2>
                    <p className="text-sm text-[#4A5568] mt-3 max-w-md">
                        Gifts dedicated to people who mattered. We never display the amount.
                    </p>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tributes.map((t, i) => {
                        const isHonor = t.tribute_type === "honor";
                        return (
                            <div
                                key={i}
                                data-testid={`tribute-card-${i}`}
                                className="bg-white border border-black/5 rounded-2xl p-5 flex items-start gap-3 hover:shadow-md transition-all"
                            >
                                <div
                                    className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                                        isHonor ? "bg-[#F2D8D5]" : "bg-[#F4F1EA]"
                                    }`}
                                    aria-hidden="true"
                                >
                                    <Heart
                                        className={`w-4 h-4 ${isHonor ? "text-[#E53935]" : "text-[#1C252E]"}`}
                                        fill={isHonor ? "#E53935" : "#1C252E"}
                                        strokeWidth={0}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className={`text-[10px] uppercase tracking-wider font-bold ${isHonor ? "text-[#E53935]" : "text-[#1C252E]"}`}>
                                        {isHonor ? "In honor of" : "In memory of"}
                                    </p>
                                    <p className="font-heading text-lg text-[#1C252E] font-medium leading-tight mt-0.5 truncate">
                                        {t.tribute_name}
                                    </p>
                                    <p className="text-xs text-[#4A5568] mt-1 truncate">
                                        from {t.donor_name}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default RecentTributes;
